/**
 * ECM enrollment-priority — Phase 3 of the ECM ML buildout. Ranks members by
 * model-predicted enrollment propensity (ecm_enrollment_predictor) so staff
 * prioritise outreach toward the undecided youth most likely to enrol.
 * Replaces the old seeded/demo predictions with real scored output.
 * Internal CBO only.
 */

import React, { useState } from 'react';
import { Target, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useECMEnrollmentPriority, type ECMPriorityMember } from '@/hooks/ml';

// Backend may serialize numeric columns as strings (Decimal → JSON); coerce
// before any formatting so one bad value can't unmount the panel.
const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};
const pct = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : `${(n * 100).toFixed(1)}%`;
};

const BAND_TONE: Record<string, string> = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-gray-100 text-gray-600',
};

export const ECMEnrollmentPriorityCard: React.FC = () => {
  const [actionableOnly, setActionableOnly] = useState(true);
  const q = useECMEnrollmentPriority(100, actionableOnly);

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">Loading enrollment priority…</CardContent>
      </Card>
    );
  }
  if (q.isError || !q.data || q.data.scope !== 'consolidated') {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          ECM enrollment priority unavailable (internal CBO only).
        </CardContent>
      </Card>
    );
  }

  const { summary, members } = q.data;
  const totals = summary.by_cbo.reduce(
    (acc, r) => {
      acc.scored += num(r.total_scored) ?? 0;
      acc.actionable += num(r.actionable) ?? 0;
      acc.high += num(r.actionable_high) ?? 0;
      acc.medium += num(r.actionable_medium) ?? 0;
      return acc;
    },
    { scored: 0, actionable: 0, high: 0, medium: 0 },
  );

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-1.5 text-xs text-gray-600">
          <Target className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
          <span>
            Model-predicted ECM enrollment propensity (ecm_enrollment_predictor).
            "Actionable" = members still undecided (pending / in outreach) where
            outreach can still change the outcome — work the high band first.
          </span>
        </div>

        {/* Summary metric chips */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Metric label="Members scored" value={fmt(totals.scored)} />
          <Metric label="Actionable (undecided)" value={fmt(totals.actionable)} />
          <Metric label="High propensity" value={fmt(totals.high)} tone="text-emerald-700" />
          <Metric label="Medium propensity" value={fmt(totals.medium)} tone="text-amber-700" />
        </div>

        {/* Filter toggle */}
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setActionableOnly(true)}
            className={`rounded-full border px-3 py-1 font-medium ${
              actionableOnly
                ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Actionable only
          </button>
          <button
            type="button"
            onClick={() => setActionableOnly(false)}
            className={`rounded-full border px-3 py-1 font-medium ${
              !actionableOnly
                ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            All scored
          </button>
        </div>

        {!members.length ? (
          <div className="py-6 text-center text-sm text-gray-500">No members match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-1.5 pr-3 font-medium">CBO</th>
                  <th className="py-1.5 pr-3 font-medium">Member</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Enroll likelihood</th>
                  <th className="py-1.5 pr-3 font-medium text-center">Priority</th>
                  <th className="py-1.5 pr-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <PriorityRow key={`${m.legacy_cbo_id}-${m.legacy_trust_member_id}`} m={m} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[10px] text-gray-500 leading-relaxed">
          Member IDs are de-identified legacy keys. Likelihood is the model's
          calibrated probability; ranking is for outreach triage only and is not a
          clinical or eligibility determination.
        </div>
      </CardContent>
    </Card>
  );
};

const Metric: React.FC<{ label: string; value: string; tone?: string }> = ({ label, value, tone }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <TrendingUp className="h-4 w-4 text-indigo-500" />
      {label}
    </div>
    <div className={`text-xl font-semibold mt-1 tabular-nums ${tone ?? 'text-gray-900'}`}>{value}</div>
  </div>
);

const PriorityRow: React.FC<{ m: ECMPriorityMember }> = ({ m }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50/40">
    <td className="py-2 pr-3 tabular-nums text-gray-700">{m.legacy_cbo_id}</td>
    <td className="py-2 pr-3 tabular-nums text-gray-500">#{m.legacy_trust_member_id}</td>
    <td className="py-2 pr-3 text-right tabular-nums font-medium text-gray-900">
      {pct(m.enroll_probability)}
    </td>
    <td className="py-2 pr-3 text-center">
      <span
        className={`rounded px-2 py-0.5 font-semibold capitalize ${
          BAND_TONE[m.priority_band] ?? 'bg-gray-100 text-gray-600'
        }`}
      >
        {m.priority_band}
      </span>
    </td>
    <td className="py-2 pr-3 text-[11px] text-gray-600 capitalize">
      {(m.current_status ?? '—').replace(/_/g, ' ')}
    </td>
  </tr>
);

export default ECMEnrollmentPriorityCard;
